import * as React from "react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";
import { Button } from "./button";

/**
 * EmptyState component for V2 interface
 *
 * A polished, reusable component for displaying empty states across the application.
 * Used for "coming soon" features, empty data states, and placeholder content.
 *
 * @param {Object} props
 * @param {React.ComponentType} props.icon - Lucide icon component to display
 * @param {string} props.title - Main heading text
 * @param {string} props.description - Supporting description text
 * @param {Object} props.primaryAction - Primary CTA configuration
 * @param {string} props.primaryAction.label - Button text
 * @param {string} [props.primaryAction.href] - Navigation link (uses react-router Link)
 * @param {Function} [props.primaryAction.onClick] - Click handler (alternative to href)
 * @param {Object} [props.secondaryAction] - Optional secondary action
 * @param {string} props.secondaryAction.label - Button text
 * @param {string} [props.secondaryAction.href] - Navigation link
 * @param {Function} [props.secondaryAction.onClick] - Click handler
 * @param {string} [props.className] - Additional CSS classes
 *
 * @example
 * <EmptyState
 *   icon={Lightbulb}
 *   title="Portfolio Insights"
 *   description="Get detailed analytics and recommendations for your portfolio."
 *   primaryAction={{ label: "View Dashboard", href: "/" }}
 * />
 */
const EmptyState = React.forwardRef(
  (
    {
      icon: Icon,
      title,
      description,
      primaryAction,
      secondaryAction,
      className,
      ...props
    },
    ref
  ) => {
    const renderAction = (action, variant = "default") => {
      if (!action) return null;

      const buttonProps = {
        variant,
        size: "default",
      };

      if (action.href) {
        return (
          <Button asChild {...buttonProps}>
            <Link to={action.href}>{action.label}</Link>
          </Button>
        );
      }

      return (
        <Button onClick={action.onClick} {...buttonProps}>
          {action.label}
        </Button>
      );
    };

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col items-center justify-center py-24 px-6",
          className
        )}
        {...props}
      >
        {/* Icon container */}
        {Icon && (
          <div className="rounded-full bg-muted/50 p-5 mb-6 ring-1 ring-border/50">
            <Icon className="h-12 w-12 text-muted-foreground" strokeWidth={1.5} />
          </div>
        )}

        {/* Title */}
        {title && (
          <h2 className="text-2xl font-semibold tracking-tight text-foreground text-center">
            {title}
          </h2>
        )}

        {/* Description */}
        {description && (
          <p className="mt-3 text-center text-muted-foreground max-w-sm leading-relaxed">
            {description}
          </p>
        )}

        {/* Action buttons */}
        {(primaryAction || secondaryAction) && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {renderAction(primaryAction, "default")}
            {renderAction(secondaryAction, "ghost")}
          </div>
        )}
      </div>
    );
  }
);

EmptyState.displayName = "EmptyState";

export { EmptyState };
